import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DiffViewer } from 'framework/DiffViewer';
import { useNavigate } from 'react-router-dom';
import { ShowSection } from 'framework/ui/layout';
import { EnumDropdown } from 'framework/form';
import useComplianceDocumentVersionFinalizeData from './useComplianceDocumentVersionFinalizeData';
import ComplianceDocumentFormFields from 'domain/compliance/complianceRegistry/shared/form/ComplianceDocumentFormFields';
import { COMPLIANCE_DOCUMENT_TYPE_VALUES } from 'domain/shared/compliance';
import { COMPLIANCE_MD_EDITOR_PATH } from '../../router';
import ComplianceDocumentFormActionButtons from 'domain/compliance/complianceRegistry/shared/buttons/actions/ComplianceDocumentFormActionButtons';
import { CollapsibleDocumentPreview, ExpandCollapseButton, useDocumentBlob } from 'domain/shared/documentStorage';

const ComplianceDocumentVersionFinalizeContent = ({ search }: { search: string }) => {
  const navigate = useNavigate();
  const { sourceDiff, canShowSourceDiff, documentName, hasComparableVersion, documentPath } =
    useComplianceDocumentVersionFinalizeData();
  const { blobUrl, loading, hasError } = useDocumentBlob(documentPath || null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!hasComparableVersion) {
      setIsExpanded(false);
    }
  }, [hasComparableVersion]);

  const renderComparisonSection = () => {
    if (!hasComparableVersion) {
      return (
        <Box>
          <CollapsibleDocumentPreview
            documentPath={documentPath || ''}
            blobUrl={blobUrl || ''}
            loading={loading}
            hasError={hasError}
            isExpanded={isExpanded}
          />

          <ExpandCollapseButton isExpanded={isExpanded} onToggle={() => setIsExpanded(prev => !prev)} />
        </Box>
      );
    }

    if (canShowSourceDiff) {
      return <DiffViewer difference={sourceDiff?.differences || []} />;
    }

    return (
      <Typography variant="body1" color="textSecondary">
        Failed to load document diff. Please try again.
      </Typography>
    );
  };

  return (
    <ShowSection sx={{ m: '0 !important', pb: '15px !important' }}>
      <Box gap={2} display="flex" flexDirection="column">
        <Typography variant="body1" color="textSecondary">
          Document name: {documentName || '-'}
        </Typography>

        <Box maxWidth={360}>
          <EnumDropdown options={COMPLIANCE_DOCUMENT_TYPE_VALUES} source="complianceType" label="" placeholder="Type" />
        </Box>
      </Box>

      <ComplianceDocumentFormFields isEditForm showDocumentMetaFields={false} showUploader={false} />

      <Box sx={{ pb: 2.25 }}>{renderComparisonSection()}</Box>

      <Box display="flex" justifyContent="flex-end">
        <ComplianceDocumentFormActionButtons
          isEditForm={false}
          publishView
          onCancel={() => navigate(`${COMPLIANCE_MD_EDITOR_PATH}${search}`)}
          onCancelPreview={() => navigate(`${COMPLIANCE_MD_EDITOR_PATH}${search}`)}
        />
      </Box>
    </ShowSection>
  );
};

export default ComplianceDocumentVersionFinalizeContent;
